import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { Button, type ButtonProps } from '@promanage/ui';
import { Icon } from './Icon';
import { loadOverlays, useOverlays } from './DeferredOverlay';

/**
 * Unsaved work in a form, and the warning on leaving with it.
 *
 * ── ONE GUARD FOR THE APP, NOT ONE PER FORM ──────────────────────────────────────────────────
 *
 * A settings page holds four forms and a record page several panels, each saved on its own. A
 * blocker per form would stack a dialog per dirty form on one click; a blocker per page would
 * forget the other three the moment one was saved. So each form reports itself here, by id, and
 * `UnsavedChangesGuard` — mounted once, in the root — asks whether ANY of them is dirty.
 *
 * ── DIRTY MEANS SOMEBODY TYPED, NOT THAT THE VALUES DIFFER ───────────────────────────────────
 *
 * `touch` is wired to the form's `onChange`, which React bubbles from every input, select and
 * textarea inside it — so a form opts in with one prop rather than a comparison of fifteen
 * `useState`s against their starting values. The cost is that typing a value and deleting it
 * again still counts, and a warning that is occasionally over-cautious is the right side to err
 * on for a form that took ten minutes to fill in.
 *
 * The transaction queues on `/financial` and `/financial/entry` keep their own `LeaveWarning`:
 * what they lose is counted drafts, and they say how many.
 */

const dirtyForms = new Set<string>();

/** How long a save button says "Saved" before it goes back to its own label. */
const SAVED_MS = 2500;

/**
 * `known`, for a form that already works out whether it differs from what is saved — it is then
 * the answer, and `touch` is not needed. A value that is typed and deleted again is not a change
 * to such a form, so it should not be one to the warning either.
 */
export function useUnsavedChanges(known?: boolean) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const dirty = known ?? touched;

  useEffect(() => {
    if (known === undefined) return;
    if (known) dirtyForms.add(id);
    else dirtyForms.delete(id);
  }, [id, known]);

  // A form that leaves the page takes its unsaved state with it — whether it went because the
  // guard let somebody leave, or because a dialog closed over it.
  useEffect(() => () => void dirtyForms.delete(id), [id]);

  useEffect(() => {
    if (savedAt === null) return;
    const timer = window.setTimeout(() => setSavedAt(null), SAVED_MS);
    return () => window.clearTimeout(timer);
  }, [savedAt]);

  const touch = useCallback(() => {
    dirtyForms.add(id);
    setTouched(true);
    setSavedAt(null);
  }, [id]);

  /**
   * Call on a successful save, BEFORE any navigation that follows it: the guard reads the
   * registry at the moment of navigating, and a form still marked dirty would block its own
   * redirect to the record it just created.
   */
  const saved = useCallback(() => {
    dirtyForms.delete(id);
    setTouched(false);
    setSavedAt(Date.now());
  }, [id]);

  /** The form was put back as it was — a Cancel, a Remove — so there is nothing to lose. */
  const reset = useCallback(() => {
    dirtyForms.delete(id);
    setTouched(false);
  }, [id]);

  return { dirty, justSaved: savedAt !== null, touch, saved, reset };
}

export type UnsavedChanges = ReturnType<typeof useUnsavedChanges>;

/**
 * Whether moving from `current` to `next` should stop and ask.
 *
 * Only a change of PAGE. Lists and tabs keep their filter and sort in the URL's search, and a form
 * does not unmount because a sort order changed beside it — asking then would teach people to
 * click Discard without reading.
 */
export function leaveIsBlocked(
  dirtyCount: number,
  current: { pathname: string },
  next: { pathname: string },
): boolean {
  return dirtyCount > 0 && current.pathname !== next.pathname;
}

/**
 * The warning, once, in the root layout.
 *
 * Both halves of `useBlocker` are used, as on the transaction queues: `shouldBlockFn` for a click
 * inside the app, `enableBeforeUnload` for closing the tab, reloading, or leaving the site.
 *
 * A change of SEARCH alone is let through — see `leaveIsBlocked`.
 */
export function UnsavedChangesGuard() {
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => leaveIsBlocked(dirtyForms.size, current, next),
    enableBeforeUnload: () => dirtyForms.size > 0,
    withResolver: true,
  });

  const overlays = useOverlays();
  const blocked = blocker.status === 'blocked';

  const stay = () => {
    if (blocker.status === 'blocked') blocker.reset();
  };
  const discard = () => {
    if (blocker.status !== 'blocked') return;
    dirtyForms.clear();
    blocker.proceed();
  };

  /*
   * THE GUARD IS EAGER; ONLY ITS DIALOG IS LATE (see `DeferredOverlay`). Blocking is what protects
   * the work, and it is in place from the first render. The dialog is almost always in memory long
   * before anybody has typed enough to be asked about it — but if the chunk could not be fetched,
   * a navigation held with nothing on screen would look like a frozen app. So the browser's own
   * confirm stands in: plain, but it asks the same question and cannot fail to appear.
   */
  useEffect(() => {
    if (!blocked || overlays) return;
    let cancelled = false;
    loadOverlays().catch(() => {
      if (cancelled) return;
      if (window.confirm(LEAVE_QUESTION)) discard();
      else stay();
    });
    return () => {
      cancelled = true;
    };
    // `stay` and `discard` read the blocker at call time; the effect is about `blocked` alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, overlays]);

  if (!blocked || !overlays) return null;

  return <overlays.LeaveDialog onStay={stay} onDiscard={discard} />;
}

/** The fallback's wording — the dialog's title and description, as one sentence. */
const LEAVE_QUESTION =
  'Leave without saving? You have changes on this page that have not been saved. Leaving now discards them.';

/**
 * A form's submit button that says "Saved" for a moment after it worked.
 *
 * For forms that SAVE IN PLACE — a settings page, a panel on a record — where nothing else on the
 * screen changes to show the write landed. A create form navigates to the new record, which is
 * confirmation enough, so it keeps a plain button.
 *
 * All three labels share one grid cell and only the current one is visible, so the button is
 * always as wide as the widest: a Save that shrinks to "Saved" and grows back moves every button
 * beside it twice.
 */
export function SaveButton({
  pending,
  saved,
  children,
  disabled,
  type = 'submit',
  ...rest
}: Omit<ButtonProps, 'children'> & { pending: boolean; saved: boolean; children: ReactNode }) {
  const showing = pending ? 'pending' : saved ? 'saved' : 'idle';
  const layer = (name: typeof showing) =>
    `col-start-1 row-start-1 inline-flex items-center justify-center gap-1.5 ${
      showing === name ? '' : 'invisible'
    }`;
  return (
    <Button type={type} disabled={disabled === true || pending} {...rest}>
      <span className="grid">
        <span className={layer('idle')}>{children}</span>
        <span className={layer('pending')}>Saving</span>
        <span className={layer('saved')}>
          <Icon name="check" size={13} />
          Saved
        </span>
      </span>
    </Button>
  );
}

/**
 * "Saved", where the form that saved has already closed.
 *
 * Most panels edit behind a Change button and fold the form away on success, so there is no save
 * button left to say it worked. This stands in the Change button's place for the same moment
 * `SaveButton` would have shown it — the new value is on screen, but a figure that changed from
 * 5,000.00 to 5,000.00 because the typing was wrong looks identical to one that never saved.
 */
export function SavedNote({ className = '' }: { className?: string }) {
  return (
    <span
      role="status"
      className={`inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-positive ${className}`}
    >
      <Icon name="check" size={13} />
      Saved
    </span>
  );
}
